<template>
  <div class="system-config">
    <div class="config-header">
      <h2>系统配置</h2>
      <p>配置网站的基本信息和相关设置</p>
    </div>

    <el-form
      ref="configForm"
      :model="configData"
      :rules="rules"
      label-width="120px"
      class="config-form"
    >
      <!-- 网站Logo -->
      <el-form-item label="网站Logo" prop="website_logo">
        <div class="upload-section">
          <el-upload
            class="logo-uploader"
            :action="uploadUrl"
            :data="{type: 'logo'}"
            :headers="uploadHeaders"
            :show-file-list="false"
            :on-success="handleLogoSuccess"
            :before-upload="beforeLogoUpload"
            :loading="logoUploading"
          >
            <img v-if="configData.website_logo" :src="configData.website_logo" class="logo-preview" />
            <i v-else class="el-icon-plus logo-uploader-icon"></i>
          </el-upload>
          <div class="upload-tip">建议尺寸：200x60px，支持jpg、png格式</div>
        </div>
      </el-form-item>

      <!-- 网站名称 -->
      <el-form-item label="网站名称" prop="website_name">
        <el-input
          v-model="configData.website_name"
          placeholder="请输入网站名称"
          maxlength="50"
          show-word-limit
        ></el-input>
      </el-form-item>

      <!-- 三方链接 -->
      <el-form-item label="三方链接" prop="third_party_config">
        <el-input
          v-model="configData.third_party_config"
          type="textarea"
          :rows="4"
          placeholder="请输入三方链接配置（JSON格式）"
        ></el-input>
        <div class="form-tip">格式示例：{"qq": "https://qq.com", "wechat": "https://wechat.com"}</div>
      </el-form-item>

      <!-- 客服二维码 -->
      <el-form-item label="客服二维码" prop="customer_service_qr">
        <div class="upload-section">
          <el-upload
            class="qr-uploader"
            :action="uploadUrl"
            :data="{type: 'customer-service-qr'}"
            :headers="uploadHeaders"
            :show-file-list="false"
            :on-success="handleCustomerQrSuccess"
            :before-upload="beforeQrUpload"
            :loading="customerQrUploading"
          >
            <img v-if="configData.customer_service_qr" :src="configData.customer_service_qr" class="qr-preview" />
            <i v-else class="el-icon-plus qr-uploader-icon"></i>
          </el-upload>
          <div class="upload-tip">建议尺寸：200x200px，支持jpg、png格式</div>
        </div>
      </el-form-item>

      <!-- 交流群二维码 -->
      <el-form-item label="交流群二维码" prop="chat_group_qr">
        <div class="upload-section">
          <el-upload
            class="qr-uploader"
            :action="uploadUrl"
            :data="{type: 'chat-group-qr'}"
            :headers="uploadHeaders"
            :show-file-list="false"
            :on-success="handleChatQrSuccess"
            :before-upload="beforeQrUpload"
            :loading="chatQrUploading"
          >
            <img v-if="configData.chat_group_qr" :src="configData.chat_group_qr" class="qr-preview" />
            <i v-else class="el-icon-plus qr-uploader-icon"></i>
          </el-upload>
          <div class="upload-tip">建议尺寸：200x200px，支持jpg、png格式</div>
        </div>
      </el-form-item>

      <!-- 操作按钮 -->
      <el-form-item>
        <el-button type="primary" @click="saveConfig" :loading="saving">保存配置</el-button>
        <el-button @click="resetForm">重置</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import { getSystemConfig, updateSystemConfig } from '@/api'

export default {
  name: 'SystemConfig',
  data() {
    return {
      configData: {
        website_logo: '',
        website_name: '',
        third_party_config: '',
        customer_service_qr: '',
        chat_group_qr: ''
      },
      rules: {
        website_name: [
          { required: true, message: '请输入网站名称', trigger: 'blur' },
          { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
        ]
      },
      uploadUrl: process.env.VUE_APP_BASE_API + '/upload/system-file',
      uploadHeaders: {
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      },
      logoUploading: false,
      customerQrUploading: false,
      chatQrUploading: false,
      saving: false
    }
  },
  mounted() {
    this.loadConfig()
  },
  methods: {
    // 加载配置
    async loadConfig() {
      try {
        const response = await getSystemConfig()
        if (response.data) {
          this.configData = { ...this.configData, ...response.data }
          // 如果third_party_config是对象，转换为JSON字符串
          if (typeof this.configData.third_party_config === 'object') {
            this.configData.third_party_config = JSON.stringify(this.configData.third_party_config, null, 2)
          }
        }
      } catch (error) {
        console.error('加载配置失败:', error)
        this.$message.error('加载配置失败')
      }
    },

    // Logo上传前验证
    beforeLogoUpload(file) {
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png'
      const isLt2M = file.size / 1024 / 1024 < 2

      if (!isJPG) {
        this.$message.error('Logo只能是 JPG/PNG 格式!')
      }
      if (!isLt2M) {
        this.$message.error('Logo大小不能超过 2MB!')
      }
      this.logoUploading = true
      return isJPG && isLt2M
    },

    // Logo上传成功
    handleLogoSuccess(response) {
      this.logoUploading = false
      if (response.success) {
        this.configData.website_logo = response.data.data.url
        this.$message.success('Logo上传成功')
      } else {
        this.$message.error(response.message || 'Logo上传失败')
      }
    },

    // 二维码上传前验证
    beforeQrUpload(file) {
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png'
      const isLt2M = file.size / 1024 / 1024 < 2

      if (!isJPG) {
        this.$message.error('二维码只能是 JPG/PNG 格式!')
      }
      if (!isLt2M) {
        this.$message.error('二维码大小不能超过 2MB!')
      }
      return isJPG && isLt2M
    },

    // 客服二维码上传成功
    handleCustomerQrSuccess(response) {
      this.customerQrUploading = false
      if (response.success) {
        this.configData.customer_service_qr = response.data.data.url
        this.$message.success('客服二维码上传成功')
      } else {
        this.$message.error(response.message || '客服二维码上传失败')
      }
    },

    // 交流群二维码上传成功
    handleChatQrSuccess(response) {
      this.chatQrUploading = false
      if (response.success) {
        this.configData.chat_group_qr = response.data.data.url
        this.$message.success('交流群二维码上传成功')
      } else {
        this.$message.error(response.message || '交流群二维码上传失败')
      }
    },

    // 保存配置
    async saveConfig() {
      this.$refs.configForm.validate(async (valid) => {
        if (valid) {
          this.saving = true
          try {
            const submitData = { ...this.configData }
            // 如果third_party_config是字符串，尝试解析为JSON
            if (typeof submitData.third_party_config === 'string' && submitData.third_party_config.trim()) {
              try {
                submitData.third_party_config = JSON.parse(submitData.third_party_config)
              } catch (e) {
                this.$message.error('三方链接配置格式不正确，请输入有效的JSON格式')
                this.saving = false
                return
              }
            }
            
            await updateSystemConfig(submitData)
            this.$message.success('配置保存成功')
          } catch (error) {
            console.error('保存配置失败:', error)
            this.$message.error('保存配置失败')
          } finally {
            this.saving = false
          }
        }
      })
    },

    // 重置表单
    resetForm() {
      this.$refs.configForm.resetFields()
      this.loadConfig()
    }
  }
}
</script>

<style scoped>
.system-config {
  max-width: 800px;
  margin: 0 auto;
}

.config-header {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #ebeef5;
}

.config-header h2 {
  margin: 0 0 10px 0;
  color: #303133;
  font-size: 24px;
}

.config-header p {
  margin: 0;
  color: #909399;
  font-size: 14px;
}

.config-form {
  background: #fff;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.upload-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.logo-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 200px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-uploader:hover {
  border-color: #409eff;
}

.logo-uploader-icon {
  font-size: 28px;
  color: #8c939d;
}

.logo-preview {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.qr-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-uploader:hover {
  border-color: #409eff;
}

.qr-uploader-icon {
  font-size: 28px;
  color: #8c939d;
}

.qr-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
  line-height: 1.4;
}

.el-form-item {
  margin-bottom: 25px;
}

.el-button {
  margin-right: 10px;
}
</style>